import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getSocket } from '../utils/socketManager';
import { getSocketBaseUrl } from '../utils/socketUtils';
// import { useWebSocketData } from './useWebSocketData'; // Removed WebSocket hook import

const API_URL = '';  // Use relative URLs

export const useEventData = (selectedView, selectedPillarId) => {
  const [restEventData, setRestEventData] = useState({
    activeData: [],
    eventListData: { data: [] },
    totalAcledData: [],
  });
  const [restIsLoading, setRestIsLoading] = useState(true);
  const [restError, setRestError] = useState(null);

  const viewId = selectedView?.id;
  const pillarId = selectedPillarId;

  // WebSocket setup
  const socketRef = useRef(null);
  const lastRefreshRef = useRef(0);
  const REFRESH_COOLDOWN_MS = 1500;

  // WebSocket subscription for events (view_id + pillar_id) - REMOVED
  // const wsFilters = useMemo(() => ({
  //   view_id: viewId,
  //   pillar_id: pillarId
  // }), [viewId, pillarId]);
  // const {
  //   data: socketEventData,
  //   isLoading: socketIsLoading,
  //   error: socketError,
  //   isConnected: socketConnected
  // } = useWebSocketData('events', { filters: wsFilters, autoRefresh: true }); // REMOVED

  // Fetch event data from REST (view_id + pillar_id)
  const fetchEventData = useCallback(async () => {
    const requestId = `${viewId}-${pillarId || 'all'}-${Date.now()}`;
    // fetchEventData called for viewId: ${viewId}, pillarId: ${pillarId}

    if (!viewId) {
      // No viewId provided, clearing event data
      // Clear data and set loading to false if no viewId
      setRestEventData({
        activeData: [],
        eventListData: { data: [] },
        totalAcledData: [],
      });
      setRestIsLoading(false);
      setRestError(null);
      return;
    }
    
    setRestIsLoading(true);
    setRestError(null);
    
    try {
      let url = `${API_URL}/api/views/${viewId}/events`;
      if (pillarId) {
        url += `?pillarId=${pillarId}`;
      }
      
      // Fetching events from URL
      const eventsResponse = await fetch(url);
      
      // Response received successfully
      
      if (!eventsResponse.ok) {
        throw new Error(`Failed to fetch event data: ${eventsResponse.status} ${eventsResponse.statusText}`);
      }
      
      const activeData = await eventsResponse.json();
      // Received event data
      
      const eventDataToSet = {
        activeData: activeData || [],
        eventListData: { data: activeData || [] }, // Assuming eventListData is directly from activeData
        totalAcledData: [] // Assuming totalAcledData is not part of this specific fetch or needs separate handling
      };
      
      // Setting event data
      setRestEventData(eventDataToSet);
    } catch (err) {
      // Error fetching event data
      setRestError(err.message);
      setRestEventData({
        activeData: [],
        eventListData: { data: [] },
        totalAcledData: []
      });
    } finally {
      setRestIsLoading(false);
      // fetchEventData completed, loading set to false
    }
  }, [viewId, pillarId]);

  // Initial fetch of event data when view or pillar changes
  useEffect(() => {
    // if (!viewId) return; // Moved condition inside fetchEventData
    fetchEventData();
  }, [viewId, pillarId, fetchEventData]); // fetchEventData is stable due to useCallback

  // Setup WebSocket subscription to auto-refresh on server updates
  useEffect(() => {
    // Track handlers for cleanup
    let activeSocket = null;
    let handleEventsUpdate = null;
    let handleDataUpdate = null;
    let handleConnect = null;

    // Clear any existing socket ref
    if (socketRef.current) {
      socketRef.current = null;
    }

    if (!viewId) {
      return;
    }

    const baseUrl = getSocketBaseUrl();
    if (!baseUrl) return;

    // Debounce connection to prevent rapid churn
    const timeoutId = setTimeout(() => {
      // Use shared socket manager
      const socket = getSocket('/events-updates');
      if (!socket) return;

      activeSocket = socket;
      socketRef.current = socket;

      // Ensure socket is connected
      if (socket.disconnected) {
        socket.connect();
      }

      handleConnect = () => {
        // Connected to events namespace
        // Optionally request initial snapshot if backend supports it
      };

      const maybeRefresh = (payload) => {
        // CRITICAL: Reject updates without valid view_id - empty broadcasts should not trigger refresh
        if (!payload) {
          console.log('[useEventData] Skipping refresh - no payload');
          return;
        }
        
        // Check if payload has view_id that matches our current view
        const payloadView = payload.view_id ?? payload.viewId;
        if (payloadView !== undefined && Number(payloadView) !== Number(viewId)) {
          console.log('[useEventData] Skipping refresh - view mismatch:', { payloadView, viewId });
          return;
        }
        
        // Debounce to avoid burst refreshes
        const now = Date.now();
        if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) return;

        console.log('[useEventData] Refreshing event data from WebSocket update');
        lastRefreshRef.current = now;
        fetchEventData();
      };

      // Direct events update payload
      handleEventsUpdate = (payload) => {
        maybeRefresh(payload);
      };

      // Generic data_update wrapper: { type: 'events', data: ..., timestamp, _meta }
      handleDataUpdate = (payload) => {
        if (payload?.type === 'events') {
          // Extract view filter from meta or data
          const dataPayload = payload?.data;
          const metaView = payload?._meta?.filters?.view_id;
          if (metaView !== undefined && Number(metaView) !== Number(viewId)) {
            return;
          }
          maybeRefresh(dataPayload);
        }
      };

      if (socket.connected) {
        handleConnect();
      }

      socket.on('connect', handleConnect);
      socket.on('events_update', handleEventsUpdate);
      socket.on('data_update', handleDataUpdate);
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timeoutId);
      if (activeSocket) {
        if (handleConnect) activeSocket.off('connect', handleConnect);
        if (handleEventsUpdate) activeSocket.off('events_update', handleEventsUpdate);
        if (handleDataUpdate) activeSocket.off('data_update', handleDataUpdate);
      }
      socketRef.current = null;
    };
  }, [viewId, pillarId, fetchEventData]);

  // Prefer websocket data if available, otherwise fallback to REST - MODIFIED
  const effectiveEventData = useMemo(() => {
    // if (socketEventData && Array.isArray(socketEventData) && socketEventData.length > 0) { // REMOVED WebSocket logic
    //   return {
    //     activeData: socketEventData,
    //     eventListData: { data: socketEventData },
    //     totalAcledData: []
    //   };
    // }
    return restEventData; // Directly use REST data
  }, [restEventData]); // Dependency updated

  const effectiveIsLoading = restIsLoading; // Simplified loading state
  const effectiveError = restError; // Simplified error state

  return {
    ...effectiveEventData,
    isLoading: effectiveIsLoading,
    error: effectiveError,
    refreshEventData: fetchEventData // Directly use fetchEventData for refresh
  };
};
